"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Person = exports.stringAttributes = exports.nonStringAttributes = exports.personAttributesWithComposedAttributes = exports.personAttributes = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const IValidatable_1 = require("./IValidatable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
const SurveyObject_1 = require("./SurveyObject");
const Weight_1 = require("./Weight");
const Uuidable_1 = require("./Uuidable");
const WorkPlace_1 = require("./WorkPlace");
const SchoolPlace_1 = require("./SchoolPlace");
const Result_type_1 = require("../../types/Result.type");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const Vehicle_1 = require("./Vehicle");
const Journey_1 = require("./Journey");
const project_config_1 = __importDefault(require("../../config/project.config"));
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.personAttributes = [
    ...Weight_1.weightableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    ...Uuidable_1.uuidableAttributeNames,
    ...CompletableAttributes_1.completableAttributeNames,
    '_sequence',
    '_color',
    'age',
    'ageGroup',
    'gender',
    'sexAssignedAtBirth',
    'drivingLicenseOwnership',
    'transitPassOwnership',
    'transitPasses',
    'hasDisability',
    'carsharingMember',
    'carsharingUser',
    'bikesharingMember',
    'bikesharingUser',
    'ridesharingMember',
    'ridesharingUser',
    'occupation',
    'schoolType',
    'schoolPlaceType',
    'studentType',
    'workerType',
    'workPlaceType',
    'jobCategory',
    'jobName',
    'isOnTheRoadWorker',
    'hasTelecommuteCompatibleJob',
    'educationalAttainment',
    'genderCustom',
    'schoolTypeOtherSpecify',
    'whoWillAnswerForThisPerson',
    'isProxy',
    'nickname',
    'contactPhoneNumber',
    'contactEmail',
    'preData'
];
exports.personAttributesWithComposedAttributes = [
    ...exports.personAttributes,
    '_workPlaces',
    '_schoolPlaces',
    '_journeys',
    '_vehicles'
];
exports.nonStringAttributes = [
    '_weights',
    '_isValid',
    '_uuid',
    ...CompletableAttributes_1.completableAttributeNames,
    '_sequence',
    'age',
    'transitPasses',
    'whoWillAnswerForThisPerson',
    'isProxy',
    'preData'
];
exports.stringAttributes = exports.personAttributes.filter((attr) => !exports.nonStringAttributes.includes(attr));
/**
 * A person is a member of a household. it can have these composed objects:
 * workPlaces, schoolPlaces, journeys, vehicles
 */
class Person extends SurveyObject_1.SurveyObject {
    constructor(params, surveyObjectsRegistry) {
        super(params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes((0, omit_1.default)(params, [
            '_workPlaces',
            '_schoolPlaces',
            '_journeys',
            '_vehicles',
            'workPlaces',
            'schoolPlaces',
            'journeys',
            'vehicles',
            '_householdUuid'
        ]), exports.personAttributes, exports.personAttributesWithComposedAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
        this.workPlaces = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._workPlaces, (params) => WorkPlace_1.WorkPlace.unserialize(params, this._surveyObjectsRegistry), this._surveyObjectsRegistry);
        this.schoolPlaces = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._schoolPlaces, (params) => SchoolPlace_1.SchoolPlace.unserialize(params, this._surveyObjectsRegistry), this._surveyObjectsRegistry);
        this.journeys = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._journeys, (params) => Journey_1.Journey.unserialize(params, this._surveyObjectsRegistry), this._surveyObjectsRegistry);
        this.vehicles = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._vehicles, (params) => Vehicle_1.Vehicle.unserialize(params, this._surveyObjectsRegistry), this._surveyObjectsRegistry);
        this.householdUuid = params._householdUuid;
        this._surveyObjectsRegistry.registerPerson(this);
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _weights() {
        return this._attributes._weights;
    }
    set _weights(value) {
        this._attributes._weights = value;
    }
    get _color() {
        return this._attributes._color;
    }
    set _color(value) {
        this._attributes._color = value;
    }
    get age() {
        return this._attributes.age;
    }
    set age(value) {
        this._attributes.age = value;
    }
    get ageGroup() {
        return this._attributes.ageGroup;
    }
    set ageGroup(value) {
        this._attributes.ageGroup = value;
    }
    get gender() {
        return this._attributes.gender;
    }
    set gender(value) {
        this._attributes.gender = value;
    }
    get sexAssignedAtBirth() {
        return this._attributes.sexAssignedAtBirth;
    }
    set sexAssignedAtBirth(value) {
        this._attributes.sexAssignedAtBirth = value;
    }
    get drivingLicenseOwnership() {
        return this._attributes.drivingLicenseOwnership;
    }
    set drivingLicenseOwnership(value) {
        this._attributes.drivingLicenseOwnership = value;
    }
    /**
     * Return whether this person has a driving license or is a potential driver
     * (driving age with unknown license ownership).
     *
     * TODO: consolidate with `hasOrUnknownDrivingLicense` in `odSurvey/helpers.ts`;
     * duplicate logic until questionnaire helpers and base object methods are unified.
     *
     * @returns `true` if the person has a license or is of driving age with unknown ownership
     */
    hasOrUnknownDrivingLicense() {
        var _a;
        const drivingLicenseOwnership = (_a = this.drivingLicenseOwnership) !== null && _a !== void 0 ? _a : 'dontKnow';
        return (drivingLicenseOwnership === 'yes' ||
            (this.age !== undefined &&
                this.age >= project_config_1.default.drivingLicenseAge &&
                drivingLicenseOwnership === 'dontKnow'));
    }
    get transitPassOwnership() {
        return this._attributes.transitPassOwnership;
    }
    set transitPassOwnership(value) {
        this._attributes.transitPassOwnership = value;
    }
    get transitPasses() {
        return this._attributes.transitPasses;
    }
    set transitPasses(value) {
        this._attributes.transitPasses = value;
    }
    get hasDisability() {
        return this._attributes.hasDisability;
    }
    set hasDisability(value) {
        this._attributes.hasDisability = value;
    }
    get carsharingMember() {
        return this._attributes.carsharingMember;
    }
    set carsharingMember(value) {
        this._attributes.carsharingMember = value;
    }
    get carsharingUser() {
        return this._attributes.carsharingUser;
    }
    set carsharingUser(value) {
        this._attributes.carsharingUser = value;
    }
    get bikesharingMember() {
        return this._attributes.bikesharingMember;
    }
    set bikesharingMember(value) {
        this._attributes.bikesharingMember = value;
    }
    get bikesharingUser() {
        return this._attributes.bikesharingUser;
    }
    set bikesharingUser(value) {
        this._attributes.bikesharingUser = value;
    }
    get ridesharingMember() {
        return this._attributes.ridesharingMember;
    }
    set ridesharingMember(value) {
        this._attributes.ridesharingMember = value;
    }
    get ridesharingUser() {
        return this._attributes.ridesharingUser;
    }
    set ridesharingUser(value) {
        this._attributes.ridesharingUser = value;
    }
    get occupation() {
        return this._attributes.occupation;
    }
    set occupation(value) {
        this._attributes.occupation = value;
    }
    get jobCategory() {
        return this._attributes.jobCategory;
    }
    set jobCategory(value) {
        this._attributes.jobCategory = value;
    }
    get jobName() {
        return this._attributes.jobName;
    }
    set jobName(value) {
        this._attributes.jobName = value;
    }
    get isOnTheRoadWorker() {
        return this._attributes.isOnTheRoadWorker;
    }
    set isOnTheRoadWorker(value) {
        this._attributes.isOnTheRoadWorker = value;
    }
    get hasTelecommuteCompatibleJob() {
        return this._attributes.hasTelecommuteCompatibleJob;
    }
    set hasTelecommuteCompatibleJob(value) {
        this._attributes.hasTelecommuteCompatibleJob = value;
    }
    get educationalAttainment() {
        return this._attributes.educationalAttainment;
    }
    set educationalAttainment(value) {
        this._attributes.educationalAttainment = value;
    }
    get genderCustom() {
        return this._attributes.genderCustom;
    }
    set genderCustom(value) {
        this._attributes.genderCustom = value;
    }
    get schoolTypeOtherSpecify() {
        return this._attributes.schoolTypeOtherSpecify;
    }
    set schoolTypeOtherSpecify(value) {
        this._attributes.schoolTypeOtherSpecify = value;
    }
    /**
     * UUID of the household member who will answer for this person.
     * Used to determine if this person's responses are provided by a proxy.
     * If this UUID differs from the person's own UUID, then isProxy should be true.
     */
    get whoWillAnswerForThisPerson() {
        return this._attributes.whoWillAnswerForThisPerson;
    }
    set whoWillAnswerForThisPerson(value) {
        this._attributes.whoWillAnswerForThisPerson = value;
    }
    get isProxy() {
        return this._attributes.isProxy;
    }
    set isProxy(value) {
        this._attributes.isProxy = value;
    }
    get nickname() {
        return this._attributes.nickname;
    }
    set nickname(value) {
        this._attributes.nickname = value;
    }
    get contactPhoneNumber() {
        return this._attributes.contactPhoneNumber;
    }
    set contactPhoneNumber(value) {
        this._attributes.contactPhoneNumber = value;
    }
    get contactEmail() {
        return this._attributes.contactEmail;
    }
    set contactEmail(value) {
        this._attributes.contactEmail = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get workPlaceType() {
        return this._attributes.workPlaceType;
    }
    set workPlaceType(value) {
        this._attributes.workPlaceType = value;
    }
    get schoolPlaceType() {
        return this._attributes.schoolPlaceType;
    }
    set schoolPlaceType(value) {
        this._attributes.schoolPlaceType = value;
    }
    get studentType() {
        return this._attributes.studentType;
    }
    set studentType(value) {
        this._attributes.studentType = value;
    }
    get workerType() {
        return this._attributes.workerType;
    }
    set workerType(value) {
        this._attributes.workerType = value;
    }
    get schoolType() {
        return this._attributes.schoolType;
    }
    set schoolType(value) {
        this._attributes.schoolType = value;
    }
    get workPlaces() {
        return this._workPlaces;
    }
    set workPlaces(value) {
        this._workPlaces = value;
    }
    get schoolPlaces() {
        return this._schoolPlaces;
    }
    set schoolPlaces(value) {
        this._schoolPlaces = value;
    }
    get journeys() {
        return this._journeys;
    }
    set journeys(value) {
        this._journeys = value;
    }
    get vehicles() {
        return this._vehicles;
    }
    set vehicles(value) {
        this._vehicles = value;
    }
    get householdUuid() {
        return this._householdUuid;
    }
    set householdUuid(value) {
        // must only be used by Household object
        this._householdUuid = value;
    }
    get household() {
        if (!this._householdUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getHousehold(this._householdUuid);
    }
    /**
     * Add a journey to this person
     */
    addJourney(journey) {
        if (!this._journeys) {
            this._journeys = [];
        }
        this._journeys.push(journey);
    }
    /**
     * Insert a journey at a specific index
     */
    insertJourney(journey, index) {
        if (!this._journeys) {
            this._journeys = [];
        }
        this._journeys.splice(index, 0, journey);
    }
    /**
     * Insert a journey after another journey with the specified UUID
     */
    insertJourneyAfterUuid(journey, afterUuid) {
        if (!this._journeys) {
            this._journeys = [];
        }
        // If array is empty, add the journey
        if (this._journeys.length === 0) {
            this._journeys.push(journey);
            return true;
        }
        const index = this._journeys.findIndex((j) => j._uuid === afterUuid);
        if (index >= 0) {
            this._journeys.splice(index + 1, 0, journey);
            return true;
        }
        // If UUID not found in non-empty array, return false
        return false;
    }
    /**
     * Insert a journey before another journey with the specified UUID
     */
    insertJourneyBeforeUuid(journey, beforeUuid) {
        if (!this._journeys) {
            this._journeys = [];
        }
        // If array is empty, add the journey
        if (this._journeys.length === 0) {
            this._journeys.push(journey);
            return true;
        }
        const index = this._journeys.findIndex((j) => j._uuid === beforeUuid);
        if (index >= 0) {
            this._journeys.splice(index, 0, journey);
            return true;
        }
        // If UUID not found in non-empty array, return false
        return false;
    }
    /**
     * Remove a journey from this person by UUID
     */
    removeJourney(journeyUuid) {
        if (!this._journeys) {
            return false;
        }
        const index = this._journeys.findIndex((journey) => journey._uuid === journeyUuid);
        if (index >= 0) {
            this._journeys.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Get a journey by UUID
     */
    getJourneyByUuid(journeyUuid) {
        if (!this._journeys) {
            return undefined;
        }
        return this._journeys.find((journey) => journey._uuid === journeyUuid);
    }
    /**
     * Find visited place by UUID in person's journeys
     * Searches through all journeys of this person to find a specific visited place
     * @param {string} uuid - UUID of the visited place to find
     * @returns {VisitedPlace | undefined} The found visited place or undefined if not found
     */
    findVisitedPlaceByUuid(uuid) {
        // Look through all journeys to find the visited place
        const journeys = this._journeys || [];
        for (const journey of journeys) {
            const visitedPlaces = journey.visitedPlaces || [];
            const foundPlace = visitedPlaces.find((vp) => vp._uuid === uuid);
            if (foundPlace) {
                return foundPlace;
            }
        }
        return undefined;
    }
    /**
     * Setup work and school places by extracting usual places from visited places
     * This ensures usual places are available even if only declared in travel diary
     */
    setupWorkAndSchoolPlaces() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        // Initialize arrays if undefined
        if (this.workPlaces === undefined) {
            this.workPlaces = [];
        }
        if (this.schoolPlaces === undefined) {
            this.schoolPlaces = [];
        }
        // Fetch existing coordinates to avoid adding them again
        const alreadyFetchedWorkCoordinatesStr = [];
        const alreadyFetchedSchoolCoordinatesStr = [];
        // Get existing work place coordinates
        for (let i = 0, countI = this.workPlaces.length; i < countI; i++) {
            const workPlace = this.workPlaces[i];
            const coordinates = (_b = (_a = workPlace.geography) === null || _a === void 0 ? void 0 : _a.geometry) === null || _b === void 0 ? void 0 : _b.coordinates;
            if (coordinates) {
                alreadyFetchedWorkCoordinatesStr.push(`${coordinates[0]},${coordinates[1]}`);
            }
        }
        // Get existing school place coordinates
        for (let i = 0, countI = this.schoolPlaces.length; i < countI; i++) {
            const schoolPlace = this.schoolPlaces[i];
            const coordinates = (_d = (_c = schoolPlace.geography) === null || _c === void 0 ? void 0 : _c.geometry) === null || _d === void 0 ? void 0 : _d.coordinates;
            if (coordinates) {
                alreadyFetchedSchoolCoordinatesStr.push(`${coordinates[0]},${coordinates[1]}`);
            }
        }
        // Get usual work visited places from all journeys
        const usualWorkVisitedPlaces = this.getUsualWorkVisitedPlaces();
        for (let i = 0, countI = usualWorkVisitedPlaces.length; i < countI; i++) {
            const visitedPlace = usualWorkVisitedPlaces[i];
            if (visitedPlace.geography !== undefined) {
                const lon = (_g = (_f = (_e = visitedPlace.geography) === null || _e === void 0 ? void 0 : _e.geometry) === null || _f === void 0 ? void 0 : _f.coordinates) === null || _g === void 0 ? void 0 : _g[0];
                const lat = (_k = (_j = (_h = visitedPlace.geography) === null || _h === void 0 ? void 0 : _h.geometry) === null || _j === void 0 ? void 0 : _j.coordinates) === null || _k === void 0 ? void 0 : _k[1];
                if (lat !== undefined && lon !== undefined) {
                    const coordinateStr = `${lon},${lat}`;
                    // Add to work places if not already present
                    if (!alreadyFetchedWorkCoordinatesStr.includes(coordinateStr)) {
                        alreadyFetchedWorkCoordinatesStr.push(coordinateStr);
                        const workPlace = WorkPlace_1.WorkPlace.unserialize(((_l = visitedPlace.place) === null || _l === void 0 ? void 0 : _l.attributes) || {}, this._surveyObjectsRegistry); // Clone
                        // Add name from visited place if needed
                        if (!workPlace.name && visitedPlace.name) {
                            workPlace.name = visitedPlace.name;
                        }
                        this.workPlaces.push(workPlace);
                    }
                }
            }
        }
        // Get usual school visited places from all journeys
        const usualSchoolVisitedPlaces = this.getUsualSchoolVisitedPlaces();
        for (let i = 0, countI = usualSchoolVisitedPlaces.length; i < countI; i++) {
            const visitedPlace = usualSchoolVisitedPlaces[i];
            if (visitedPlace.geography !== undefined) {
                const lon = (_p = (_o = (_m = visitedPlace.geography) === null || _m === void 0 ? void 0 : _m.geometry) === null || _o === void 0 ? void 0 : _o.coordinates) === null || _p === void 0 ? void 0 : _p[0];
                const lat = (_s = (_r = (_q = visitedPlace.geography) === null || _q === void 0 ? void 0 : _q.geometry) === null || _r === void 0 ? void 0 : _r.coordinates) === null || _s === void 0 ? void 0 : _s[1];
                if (lat !== undefined && lon !== undefined) {
                    const coordinateStr = `${lon},${lat}`;
                    // Add to school places if not already present
                    if (!alreadyFetchedSchoolCoordinatesStr.includes(coordinateStr)) {
                        alreadyFetchedSchoolCoordinatesStr.push(coordinateStr);
                        const schoolPlace = SchoolPlace_1.SchoolPlace.unserialize(((_t = visitedPlace.place) === null || _t === void 0 ? void 0 : _t.attributes) || {}, this._surveyObjectsRegistry); // Clone
                        // Add name from visited place if needed
                        if (!schoolPlace.name && visitedPlace.name) {
                            schoolPlace.name = visitedPlace.name;
                        }
                        this.schoolPlaces.push(schoolPlace);
                    }
                }
            }
        }
    }
    /**
     * Get usual work visited places from all journeys
     * @returns Array of visited places with workUsual activity only
     */
    getUsualWorkVisitedPlaces() {
        const workVisitedPlaces = [];
        const journeys = this.journeys || [];
        for (const journey of journeys) {
            const visitedPlaces = journey.visitedPlaces || [];
            for (const visitedPlace of visitedPlaces) {
                // Check if this is a work-related activity at usual place
                if (visitedPlace.activity === 'workUsual') {
                    workVisitedPlaces.push(visitedPlace);
                }
            }
        }
        return workVisitedPlaces;
    }
    /**
     * Get usual school visited places from all journeys
     * @returns Array of visited places with schoolUsual activity only
     */
    getUsualSchoolVisitedPlaces() {
        const schoolVisitedPlaces = [];
        const journeys = this.journeys || [];
        for (const journey of journeys) {
            const visitedPlaces = journey.visitedPlaces || [];
            for (const visitedPlace of visitedPlaces) {
                // Check if this is a school-related activity at usual place
                if (visitedPlace.activity === 'schoolUsual') {
                    schoolVisitedPlaces.push(visitedPlace);
                }
            }
        }
        return schoolVisitedPlaces;
    }
    /**
     * Creates a Person object from sanitized parameters
     * @param {ExtendedPersonAttributes | SerializedExtendedPersonAttributes} params - Sanitized person parameters
     * @returns {Person} New Person instance
     */
    static unserialize(params, surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new Person(flattenedParams, surveyObjectsRegistry);
    }
    /**
     * Factory that validates input from an interview and makes
     * sure types and required fields are valid before returning a new object
     * Custom attributes must be validated in each survey project
     * @param dirtyParams
     * @returns Person | Error[]
     */
    static create(dirtyParams, surveyObjectsRegistry) {
        const errors = Person.validateParams(dirtyParams);
        const person = errors.length === 0 ? new Person(dirtyParams, surveyObjectsRegistry) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(person);
    }
    /**
     * Validates attributes types for Person.
     * @param dirtyParams The parameters to validate.
     * @param displayName The name of the object to validate, for error display
     * @returns Error[] TODO: specialize this error class
     */
    static validateParams(dirtyParams, displayName = 'Person') {
        const errors = [];
        // Validate params object:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        // Validate _uuid:
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams));
        // Validate _isValid:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        errors.push(...SurveyObject_1.SurveyObject.validateCompletableParams(dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('_sequence', dirtyParams._sequence, displayName));
        // Validate _weights:
        errors.push(...(0, Weight_1.validateWeights)(dirtyParams._weights));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('age', dirtyParams.age, displayName));
        for (let i = 0, countI = exports.stringAttributes.length; i < countI; i++) {
            const stringAttribute = exports.stringAttributes[i];
            errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(stringAttribute, dirtyParams[stringAttribute], displayName));
        }
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isArrayOfStrings('transitPasses', dirtyParams.transitPasses, displayName));
        // Validate new attributes
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('whoWillAnswerForThisPerson', dirtyParams.whoWillAnswerForThisPerson, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('isProxy', dirtyParams.isProxy, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('preData', dirtyParams.preData, displayName, false));
        const workPlacesAttributes = dirtyParams._workPlaces !== undefined ? dirtyParams._workPlaces : [];
        for (let i = 0, countI = workPlacesAttributes.length; i < countI; i++) {
            const workPlaceAttributes = workPlacesAttributes[i];
            errors.push(...WorkPlace_1.WorkPlace.validateParams(workPlaceAttributes, `WorkPlace ${i}`));
        }
        const schoolPlacesAttributes = dirtyParams._schoolPlaces !== undefined ? dirtyParams._schoolPlaces : [];
        for (let i = 0, countI = schoolPlacesAttributes.length; i < countI; i++) {
            const schoolPlaceAttributes = schoolPlacesAttributes[i];
            errors.push(...SchoolPlace_1.SchoolPlace.validateParams(schoolPlaceAttributes, `SchoolPlace ${i}`));
        }
        const journeysAttributes = dirtyParams._journeys !== undefined ? dirtyParams._journeys : [];
        for (let i = 0, countI = journeysAttributes.length; i < countI; i++) {
            const journeyAttributes = journeysAttributes[i];
            errors.push(...Journey_1.Journey.validateParams(journeyAttributes, `Journey ${i}`));
        }
        const vehiclesAttributes = dirtyParams._vehicles !== undefined ? dirtyParams._vehicles : [];
        for (let i = 0, countI = vehiclesAttributes.length; i < countI; i++) {
            const vehicleAttributes = vehiclesAttributes[i];
            errors.push(...Vehicle_1.Vehicle.validateParams(vehicleAttributes, `Vehicle ${i}`));
        }
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_householdUuid', dirtyParams._householdUuid, displayName));
        return errors;
    }
    /**
     * Assign color to person from the configured color palette
     * Assigns a unique color to each person for visualization purposes, cycling through the palette
     * @param {number} personIndex - Index of the person in the household (for color cycling)
     * @returns {void}
     */
    assignColor(personIndex) {
        const colorsPalette = project_config_1.default.personColorsPalette;
        // Cycle through the color palette if there are more persons than colors
        const colorIndex = personIndex % colorsPalette.length;
        const assignedColor = colorsPalette[colorIndex];
        // Assign color to person
        this._color = assignedColor;
    }
}
exports.Person = Person;
Person._confidentialAttributes = [
    // these attributes should be hidden when exporting
    'contactPhoneNumber',
    'contactEmail',
    'nickname',
    'preData'
];
//# sourceMappingURL=Person.js.map